import { supabase } from '@/integrations/supabase/client';
import { translationService } from './translationService';

export interface TranslationField {
  originalField: string;
  arField: string;
  trField: string;
}

export interface TranslationConfig {
  table: string;
  idField: string;
  fields: TranslationField[];
}

export class DatabaseTranslationService {
  private static instance: DatabaseTranslationService;

  public static getInstance(): DatabaseTranslationService {
    if (!DatabaseTranslationService.instance) {
      DatabaseTranslationService.instance = new DatabaseTranslationService();
    }
    return DatabaseTranslationService.instance;
  }

  /**
   * Get localized content for a specific field
   * @param originalText - The original text (usually in English)
   * @param translatedText - The translated text (ar or tr)
   * @param currentLanguage - Current language context
   * @returns The appropriate text based on language
   */
  getLocalizedText(
    originalText: string,
    translatedText: string | null | undefined,
    currentLanguage: string
  ): string {
    if (currentLanguage === 'en' || !translatedText || (typeof translatedText === 'string' && translatedText.trim() === '')) {
      return originalText;
    }
    return translatedText;
  }

  /**
   * Check if translation is needed for a field
   * @param originalText - Original text
   * @param translatedText - Translated text
   * @param targetLanguage - Target language
   * @returns boolean indicating if translation is needed
   */
  needsTranslation(
    originalText: string,
    translatedText: string | null | undefined,
    targetLanguage: string
  ): boolean {
    console.log('needsTranslation check:', {
      originalText: originalText?.substring(0, 50) + '...',
      translatedText: translatedText?.substring(0, 50) + '...',
      targetLanguage,
      hasOriginalText: !!originalText,
      originalTextTrimmed: originalText?.trim(),
      hasTranslatedText: !!translatedText,
      translatedTextTrimmed: translatedText?.trim()
    });
    
    if (targetLanguage === 'en' || !originalText || (typeof originalText === 'string' && originalText.trim() === '')) {
      console.log('Translation not needed: English language or empty original text');
      return false;
    }
    
    const needsTranslation = !translatedText || (typeof translatedText === 'string' && translatedText.trim() === '');
    console.log('Translation needed:', needsTranslation);
    return needsTranslation;
  }

  /**
   * Translate and save a single field for a record
   * @param config - Translation configuration
   * @param recordId - Record ID to update
   * @param field - Field to translate
   * @param originalText - Original text to translate
   * @param targetLanguage - Target language ('ar' or 'tr')
   * @returns Promise<boolean> - Success status
   */
  async translateAndSaveField(
    config: TranslationConfig,
    recordId: string,
    field: TranslationField,
    originalText: string,
    targetLanguage: 'ar' | 'tr'
  ): Promise<{ success: boolean; translatedText?: string }> {
    try {
      console.log('translateAndSaveField called:', {
        table: config.table,
        recordId,
        field: field.originalField,
        targetField: field[`${targetLanguage}Field`],
        originalText: originalText?.substring(0, 50) + '...',
        targetLanguage
      });
      
      // Check if translation is needed
      if (!this.needsTranslation(originalText, null, targetLanguage)) {
        console.log('Translation not needed, skipping');
        return { success: true };
      }

      console.log('Starting translation...');
      // Translate the text
      const translatedText = await translationService.translateText(
        originalText,
        targetLanguage,
        'en'
      );

      console.log('Translation result:', {
        original: originalText?.substring(0, 50) + '...',
        translated: translatedText?.substring(0, 50) + '...'
      });

      // Update the database
      const updateData: any = {};
      updateData[field[`${targetLanguage}Field`]] = translatedText;

      console.log('Updating database with:', updateData);
      const { error } = await supabase
        .from(config.table)
        .update(updateData)
        .eq(config.idField, recordId);

      if (error) {
        console.error(`Error updating ${config.table} translation:`, error);
        return { success: false };
      }

      console.log('Database update successful');
      return { success: true, translatedText };
    } catch (error) {
      console.error('Translation and save failed:', error);
      return { success: false };
    }
  }

  /**
   * Translate and save multiple fields for a record
   * @param config - Translation configuration
   * @param recordId - Record ID to update
   * @param record - Record data with original fields
   * @param targetLanguage - Target language ('ar' or 'tr')
   * @returns Promise<boolean> - Success status
   */
  async translateAndSaveRecord(
    config: TranslationConfig,
    recordId: string,
    record: any,
    targetLanguage: 'ar' | 'tr'
  ): Promise<boolean> {
    try {
      const updateData: any = {};

      // Process each field that needs translation
      for (const field of config.fields) {
        const originalText = record[field.originalField];
        
        if (this.needsTranslation(originalText, null, targetLanguage)) {
          const translatedText = await translationService.translateText(
            originalText,
            targetLanguage,
            'en'
          );
          updateData[field[`${targetLanguage}Field`]] = translatedText;
        }
      }

      // If no translations needed, return success
      if (Object.keys(updateData).length === 0) {
        return true;
      }

      // Update the database
      const { error } = await supabase
        .from(config.table)
        .update(updateData)
        .eq(config.idField, recordId);

      if (error) {
        console.error(`Error updating ${config.table} translations:`, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Batch translation and save failed:', error);
      return false;
    }
  }

  /**
   * Get localized record with automatic translation if needed
   * @param config - Translation configuration
   * @param record - Record data
   * @param currentLanguage - Current language
   * @param autoTranslate - Whether to automatically translate missing fields
   * @returns Promise<Record> - Localized record
   */
  async getLocalizedRecord(
    config: TranslationConfig,
    record: any,
    currentLanguage: string,
    autoTranslate: boolean = false
  ): Promise<any> {
    console.log('getLocalizedRecord called for record:', record.id, 'language:', currentLanguage);
    
    // If English, return the original record
    if (currentLanguage === 'en') {
      console.log('Language is English, returning original record');
      return record;
    }

    // Create a copy of the record for localization
    const localizedRecord = { ...record };

    for (const field of config.fields) {
      const originalText = record[field.originalField];
      const translatedText = record[field[`${currentLanguage === 'ar' ? 'ar' : 'tr'}Field`]];

      console.log('Processing field:', {
        fieldName: field.originalField,
        originalText: originalText?.substring(0, 50) + '...',
        translatedText: translatedText?.substring(0, 50) + '...',
        currentLanguage
      });

      // Get the appropriate text
      localizedRecord[field.originalField] = this.getLocalizedText(
        originalText,
        translatedText,
        currentLanguage
      );

             // Auto-translate if needed and enabled
       if (autoTranslate && currentLanguage !== 'en' && this.needsTranslation(originalText, translatedText, currentLanguage)) {
         console.log('Auto-translation needed for field:', field.originalField);
         const targetLanguage = currentLanguage as 'ar' | 'tr';
         const translationResult = await this.translateAndSaveField(
           config,
           record[config.idField],
           field,
           originalText,
           targetLanguage
         );
         
         console.log('Translation result:', translationResult);
         
         if (translationResult.success && translationResult.translatedText) {
           console.log('Using translated text directly:', translationResult.translatedText.substring(0, 50) + '...');
           localizedRecord[field.originalField] = translationResult.translatedText;
         } else {
           console.warn('Translation failed or no translated text returned, using original text');
           localizedRecord[field.originalField] = originalText;
         }
       } else {
         console.log('Auto-translation not needed for field:', field.originalField);
       }
    }

    return localizedRecord;
  }

  /**
   * Get localized records with automatic translation if needed
   * @param config - Translation configuration
   * @param records - Array of records
   * @param currentLanguage - Current language
   * @param autoTranslate - Whether to automatically translate missing fields
   * @returns Promise<Record[]> - Array of localized records
   */
  async getLocalizedRecords(
    config: TranslationConfig,
    records: any[],
    currentLanguage: string,
    autoTranslate: boolean = false
  ): Promise<any[]> {
    console.log('getLocalizedRecords called with:', {
      recordsCount: records.length,
      currentLanguage,
      autoTranslate,
      configTable: config.table
    });
    
    const localizedRecords = await Promise.all(
      records.map(record => this.getLocalizedRecord(config, record, currentLanguage, autoTranslate))
    );

    console.log('getLocalizedRecords completed, returning', localizedRecords.length, 'records');
    return localizedRecords;
  }
}

// Export singleton instance
export const databaseTranslationService = DatabaseTranslationService.getInstance();

/**
 * Create a translation configuration dynamically based on available fields
 * @param table - Table name
 * @param idField - ID field name
 * @param availableFields - Array of field names that exist in the table
 * @returns TranslationConfig
 */
export function createTranslationConfig(
  table: string,
  idField: string,
  availableFields: string[]
): TranslationConfig {
  const fields: TranslationField[] = [];

  // Check for name field translations
  if (availableFields.includes('name_ar') && availableFields.includes('name_tr')) {
    fields.push({
      originalField: 'name',
      arField: 'name_ar',
      trField: 'name_tr'
    });
  }

  // Check for description field translations
  if (availableFields.includes('description_ar') && availableFields.includes('description_tr')) {
    fields.push({
      originalField: 'description',
      arField: 'description_ar',
      trField: 'description_tr'
    });
  }

  // Check for title field translations
  if (availableFields.includes('title_ar') && availableFields.includes('title_tr')) {
    fields.push({
      originalField: 'title',
      arField: 'title_ar',
      trField: 'title_tr'
    });
  }

  // Check for content field translations
  if (availableFields.includes('content_ar') && availableFields.includes('content_tr')) {
    fields.push({
      originalField: 'content',
      arField: 'content_ar',
      trField: 'content_tr'
    });
  }

  return {
    table,
    idField,
    fields
  };
}

/**
 * Get table schema information to determine available fields
 * @param table - Table name
 * @returns Promise<string[]> - Array of available field names
 */
export async function getTableFields(table: string): Promise<string[]> {
  try {
    // Query the table with a limit of 1 to get schema information
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .limit(1);

    if (error) {
      console.error(`Error getting schema for table ${table}:`, error);
      return [];
    }

    if (data && data.length > 0) {
      return Object.keys(data[0]);
    }

    return [];
  } catch (error) {
    console.error(`Error getting table fields for ${table}:`, error);
    return [];
  }
}

/**
 * Create translation configuration for a table dynamically
 * @param table - Table name
 * @param idField - ID field name (default: 'id')
 * @returns Promise<TranslationConfig>
 */
export async function createDynamicTranslationConfig(
  table: string,
  idField: string = 'id'
): Promise<TranslationConfig> {
  const availableFields = await getTableFields(table);
  return createTranslationConfig(table, idField, availableFields);
}

/**
 * Create a simple translation configuration for just the name field
 * @param table - Table name
 * @param idField - ID field name (default: 'id')
 * @returns TranslationConfig
 */
export function createNameTranslationConfig(
  table: string,
  idField: string = 'id'
): TranslationConfig {
  return {
    table,
    idField,
    fields: [
      {
        originalField: 'name',
        arField: 'name_ar',
        trField: 'name_tr'
      }
    ]
  };
}
